import React, { FC, useContext, useEffect, useMemo } from 'react';
import DataConsumptionContext from '../../../../../contexts/Billing/DataConsumptionContext';
import DataConsumptionMetricContext from '../../../../../contexts/Billing/DataConsumptionMetricContext';
import { InfoPaper } from '../../../components/ConsumptionData/InfoPaper';
import { ConsolidatedPaper } from '../../../components/ConsumptionData/ConsolidatedPaper';
import { LeftInfoPaperArea, RightInfoPaperArea } from '../../styles';
import { useContentLocalizer } from '../../../../../hooks/useContentLocalizer';
import { getOrdinal } from '../../../../../utils/strings';
import { roundNextHundredth } from '../../../../../utils/numbers';
import { toUTCDateString } from '../../../../../utils/date';
import { BillingMetrics } from '../../../../../shared/BillingMetrics';
import { trackEvent } from '../../../../../api/Billing/Tracking';
import { useBillingPlanUsageContext } from '../../../../../contexts/Billing/BillingPlanUsageContext';
import { billingRoutes } from '../../../../../routes/BillingRoutes';
import {
  action,
  REPORT_MEU_CONSUMPTION_OPEN,
  REPORT_MEU_PLAN_CONSUMPTION_CHART_HOVER,
  REPORT_MEU_PLAN_CONSUMPTION_TOOLTIP_HOVER,
  REPORT_MEU_TITLE_TOOLTIP_HOVER,
  REPORT_MEU_WEEKLY_CONSUMPTION_CHART_HOVER,
} from '../../../../../shared/SegmentEvents';
import colors from '../../../../../_charts.module.scss';

const MeuConsumptionComponent: FC = () => {
  const ROUND_UP_PERCENT = 1.25;

  const { dateToSearch, hasEnabledMetric, tenant, loggedUser, trackingProps, periodInfo } = useContext(DataConsumptionContext);
  const { compiledMeuConsumptionDataState, isMeuLoading, consolidatedMeuApiRequisition, meuMetricIds, localizationMeu } =
    useContext(DataConsumptionMetricContext);
  const { planStatus } = useBillingPlanUsageContext();
  const consolidatedMeuConsumption = compiledMeuConsumptionDataState.consolidatedMeuConsumption;

  const consolidatedContainsDataOnSelectedMonth =
    compiledMeuConsumptionDataState.consolidatedContainsDataOnSelectedMonth;

  useEffect(() => {
    consolidatedMeuApiRequisition(dateToSearch, meuMetricIds, hasEnabledMetric);
  }, [dateToSearch]);

  const content = useContentLocalizer(localizationMeu.dashLocalization);

  const getLeftChartLabel = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage
      ? content.formatString(content.leftChart.labels.msg)
      : content.formatString(content.leftChart.labels.miu);
  };

  const getLeftChartCustomLabel = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage
      ? content.formatString(content.leftChart.customLabels.msg)
      : content.formatString(content.leftChart.customLabels.miu);
  };

  const getCustomColor = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage
      ? colors.Brand
      : colors.System;
  };

  const arrWeekItems = consolidatedMeuConsumption.map((item) => item.weekItems);
  const arrLeftChartTotal = consolidatedMeuConsumption.map((item) => item.total);
  const arrLeftChartCustomLabel = consolidatedMeuConsumption.map((item) => {
    return {
      backgroundColor: getCustomColor(item.metric.metricId),
      text: getLeftChartCustomLabel(item.metric.metricId),
    };
  });

  const arrLeftChartLabel = consolidatedMeuConsumption.map((item) => getLeftChartLabel(item.metric.metricId));
  const arrLeftChartColor = arrLeftChartCustomLabel.map((item) => item.backgroundColor);

  let chartTooltipCount = 0;
  const leftChartData = {
    chartType: 'BarChart',
    title: content.leftChart.title,
    tooltipTrackEvent: () => {
      if (chartTooltipCount < 1) {
        trackEvent(REPORT_MEU_PLAN_CONSUMPTION_TOOLTIP_HOVER, tenant, loggedUser, {
          ...trackingProps,
          action: action.HOVER,
        });
        chartTooltipCount++;
      }
    },
    labels: arrLeftChartLabel,
    customLabels: arrLeftChartCustomLabel,
    datasets: [
      {
        data: arrLeftChartTotal,
        backgroundColor: arrLeftChartColor,
      },
    ],
  };

  let leftCount = 0;
  const arrTotal = arrLeftChartTotal;
  const maxArrTotal = Math.max(...arrTotal) > 0 ? Math.max(...arrTotal) : 1;
  const leftChartOptions = {
    maintainAspectRatio: false,
    barThickness: 15,
    locale: content.locale,
    onHover: function (evt) {
      if (evt.type === 'mousemove' && leftCount < 1) {
        trackEvent(REPORT_MEU_PLAN_CONSUMPTION_CHART_HOVER, tenant, loggedUser, trackingProps);
        leftCount++;
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: consolidatedContainsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxArrTotal * ROUND_UP_PERCENT) }
        : { min: 0 },
    },
  };

  const getRightChartCustomLabel = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage
      ? content.formatString(content.rightChart.customLabels.msg)
      : content.formatString(content.rightChart.customLabels.miu);
  };

  const weekItemsLabel = useMemo(() => {
    return arrWeekItems[0].map((item) => {
      return content.formatString(content.rightChart.period, {
        startDate: toUTCDateString(item.startDateReference, content.locale, false),
        endDate: toUTCDateString(item.endDateReference, content.locale, false),
      });
    });
  }, [arrWeekItems]);

  const labels = weekItemsLabel;

  const arrRightChartCustomLabel = consolidatedMeuConsumption.map((item) => {
    return {
      backgroundColor: getCustomColor(item.metric.metricId),
      text: getLeftChartCustomLabel(item.metric.metricId),
    };
  });

  const arrRightChartDataset = consolidatedMeuConsumption.map((item) => {
    return {
      label: getRightChartCustomLabel(item.metric.metricId),
      data: item.weekItems.map((item) => item.total),
      backgroundColor: getCustomColor(item.metric.metricId),
    };
  });

  const rightChartData = {
    chartType: 'BarChart',
    title: content.rightChart.title,
    labels,
    customLabels: arrRightChartCustomLabel,

    datasets: arrRightChartDataset,

    warningLabelBold: content.rightChart.warningLabel.bold,
    warningLabelRegular: content.rightChart.warningLabel.regular,
  };

  let rightCount = 0;
  const arrWeekTotal = arrWeekItems.map((arrItem) => arrItem.map((item) => item.total)).flat(1);
  const maxArrWeekTotal = Math.max(...arrWeekTotal) > 0 ? Math.max(...arrWeekTotal) : 1;
  const rightChartOptions = {
    maintainAspectRatio: false,
    barThickness: 8,
    locale: content.locale,
    onHover: function (evt) {
      if (evt.type === 'mousemove' && rightCount < 1) {
        trackEvent(REPORT_MEU_WEEKLY_CONSUMPTION_CHART_HOVER, tenant, loggedUser, trackingProps);
        rightCount++;
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: consolidatedContainsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxArrWeekTotal * ROUND_UP_PERCENT) }
        : { min: 0 },
    },
  };

  const redraw = false;
  const leftChart = { leftChartData, leftChartOptions, redraw };
  const rightChart = { rightChartData, rightChartOptions, redraw };

  let titleTooltipCount = 0;

  const header = {
    title: content.header.title,
    period: periodInfo,
    subtitle: content.header.description,
    learnMore: content.header.learnMore,
    modalContent: content.header.modalContent,
    buttonTitle: content.header.showMoreButton,
    pathname: billingRoutes.meuConsumption,
    idTest: 'show-more-report-meu-consumption',
    buttonTrackEvent: () =>
      trackEvent(REPORT_MEU_CONSUMPTION_OPEN, tenant, loggedUser, { ...trackingProps, action: action.CLICK }),
    tooltipTrackEvent: () => {
      if (titleTooltipCount < 1) {
        trackEvent(REPORT_MEU_TITLE_TOOLTIP_HOVER, tenant, loggedUser, { ...trackingProps, action: action.HOVER });
        titleTooltipCount++;
      }
    },
  };

  const consolidatedMetric = meuMetricIds.map((metricId) => {
    return {
      metricId: metricId,
      total: consolidatedMeuConsumption.find((item) => item.metric.metricId === metricId)?.total || 0,
      topWeekNumber: consolidatedMeuConsumption.find((item) => item.metric.metricId === metricId)?.topWeekNumber || 0,
      weeklyAverage: consolidatedMeuConsumption.find((item) => item.metric.metricId === metricId)?.weeklyAverage || 0,
    };
  });

  const metricMEU = useMemo(() =>
    consolidatedMeuConsumption.find(item => item.metric.metricId === BillingMetrics.monthlyEngagedUser),
    [consolidatedMeuConsumption]
  );

  const metricMSG = useMemo(() =>
    consolidatedMeuConsumption.find(item => item.metric.metricId === BillingMetrics.activeMessage),
    [consolidatedMeuConsumption]
  );

  const totalMeu = metricMEU?.total ?? 0;
  const totalMsg = metricMSG?.total ?? 0;
  const topWeekNumberMeu = metricMEU?.topWeekNumber ?? 0;
  const topWeekNumberMsg = metricMSG?.topWeekNumber ?? 0;
  const weeklyAverageMeu = metricMEU?.weeklyAverage ?? 0;
  const weeklyAverageMsg = metricMSG?.weeklyAverage ?? 0;

  const leftInfoPaper = useMemo(() => {
    return (
      <LeftInfoPaperArea>
        {totalMeu >= 0 && (
          <InfoPaper
            justifyContent={'space-between'}
            classNameTitle={'flex-start-margin'}
            icon={'user-engaged'}
            tooltipIcon={'info'}
            tooltipText={content.leftInfoPaper.totalMiu.tooltipText}
            title={consolidatedContainsDataOnSelectedMonth ? totalMeu.toLocaleString(content.locale) : '--'}
            text={content.leftInfoPaper.totalMiu.description}
          ></InfoPaper>
        )}
        {totalMsg >= 0 && (
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'message-talk'}
            title={consolidatedContainsDataOnSelectedMonth ? totalMsg.toLocaleString(content.locale) : '--'}
            text={content.leftInfoPaper.totalMsg.description}
          ></InfoPaper>
        )}
        {topWeekNumberMsg >= 0 && (
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'calendar'}
            title={
              consolidatedContainsDataOnSelectedMonth
                ? content.formatString(
                  content.leftInfoPaper.topWeekNumberMsg.label,
                  getOrdinal(topWeekNumberMsg, content.locale, 'f'),
                )
                : '--'
            }
            text={content.leftInfoPaper.topWeekNumberMsg.description}
            infoPaperType={'weekly'}
            bdsPaper={false}
          ></InfoPaper>
        )}
      </LeftInfoPaperArea>
    );
  }, [planStatus.current, compiledMeuConsumptionDataState]);

  const rightInfoPapers = useMemo(() => {
    return (
      <RightInfoPaperArea>
        {weeklyAverageMeu >= 0 && (
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'user-engaged'}
            title={consolidatedContainsDataOnSelectedMonth ? weeklyAverageMeu.toLocaleString(content.locale) : '--'}
            text={content.rightInfoPaper.weeklyMiuAverage.description}
            bdsPaper={false}
          ></InfoPaper>
        )}
        {weeklyAverageMsg >= 0 && (
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'message-talk'}
            title={consolidatedContainsDataOnSelectedMonth ? weeklyAverageMsg.toLocaleString(content.locale) : '--'}
            text={content.rightInfoPaper.weeklyMsgAverage.description}
            bdsPaper={false}
          ></InfoPaper>
        )}
        {topWeekNumberMeu >= 0 && (
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'calendar'}
            title={
              consolidatedContainsDataOnSelectedMonth
                ? content.formatString(
                  content.rightInfoPaper.topWeekNumberMiu.label,
                  getOrdinal(topWeekNumberMeu, content.locale, 'f'),
                )
                : '--'
            }
            text={content.rightInfoPaper.topWeekNumberMiu.description}
            infoPaperType={'weekly'}
            bdsPaper={false}
          ></InfoPaper>
        )}
      </RightInfoPaperArea>
    );
  }, [compiledMeuConsumptionDataState]);

  return (
    <ConsolidatedPaper
      header={header}
      leftChart={leftChart}
      leftInfoPapers={leftInfoPaper}
      rightChart={rightChart}
      rightInfoPapers={rightInfoPapers}
      isLoading={isMeuLoading}
    ></ConsolidatedPaper>
  );
};

export default MeuConsumptionComponent;